import { events } from "@constants/tracking";
import { FeedbackDialog } from "@features/feedback/components/dialogs/FeedbackDialog";
import { defaultFeedbackSubtitle } from "@features/feedback/constants";
import { useDevice } from "@hooks/useDevice";
import { trackCustomEvent } from "analytics";
import { router } from "expo-router";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconButton, Menu } from "react-native-paper";
import useAIFeedbackStore from "../hooks/useAIFeedbackStore";
import useSessionStore from "../hooks/useSessionStore";

type FeedbackType = "positive" | "negative" | "reportIssue";

type UseAIFeedbackMenuProps = {
	itemId: number;
	featureName: "AI Feedback" | "AI Hint";
	disabled?: boolean;
};

export const AIFeedbackMenu = ({
	itemId,
	featureName,
	disabled,
}: UseAIFeedbackMenuProps) => {
	const { t } = useTranslation();
	const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
	const [feedbackType, setFeedbackType] = useState<FeedbackType>();
	const [menuVisible, setMenuVisible] = useState(false);

	const { isMobile } = useDevice();

	const itemAIFeedback = useAIFeedbackStore(
		(store) => store.aiResponse?.[itemId],
	);
	const sessionId = useSessionStore((state) => state.id);

	const handleFeedback = useCallback(
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
		(type: FeedbackType) => {
			if (isMobile) {
				router.push({
					pathname: "/feedback",
					params: {
						view: "session",
						itemId: String(itemId),
						featureName,
						feedbackTitle:
							type === "reportIssue" ? t("dialog.report.title") : undefined,
						feedbackSubtitle:
							type === "reportIssue"
								? t("dialog.report.body")
								: t(defaultFeedbackSubtitle),
						feedbackIcon: type === "reportIssue" ? "flag-outline" : undefined,
						itemFeedbackRating: type === "reportIssue" ? "negative" : type,
						sessionId: String(sessionId),
						threadId: itemAIFeedback?.runDetails?.threadId,
						runId: itemAIFeedback?.runDetails?.runId,
						type: type === "reportIssue" ? "Content Flagged" : undefined,
					},
				});
			} else {
				setFeedbackType(type);
				setShowFeedbackDialog(true);
			}
			setMenuVisible(false);
			trackCustomEvent({
				eventName:
					type === "reportIssue"
						? events.aiFeedbackReport
						: type === "positive"
							? events.aiFeedbackPositive
							: events.aiFeedbackNegative,
				itemId,
			});
		},
		[
			itemId,
			itemAIFeedback?.runDetails?.threadId,
			itemAIFeedback?.runDetails?.runId,
			sessionId,
			t,
			featureName,
			isMobile,
		],
	);
	const handleDismissFeedback = useCallback(() => {
		setShowFeedbackDialog(false);
	}, []);

	return (
		<>
			<Menu
				visible={menuVisible}
				onDismiss={() => setMenuVisible(false)}
				anchor={
					<IconButton
						icon="dots-vertical"
						size={18}
						onPress={() => setMenuVisible(true)}
						disabled={disabled}
						style={{ margin: 0 }}
					/>
				}
				anchorPosition="bottom"
			>
				<Menu.Item
					leadingIcon="thumb-up-outline"
					onPress={() => handleFeedback("positive")}
					title={t("report.sessionAnalysis.menuActions.positiveFeedback")}
				/>
				<Menu.Item
					leadingIcon="thumb-down-outline"
					onPress={() => handleFeedback("negative")}
					title={t("report.sessionAnalysis.menuActions.negativeFeedback")}
				/>
				<Menu.Item
					leadingIcon="flag-outline"
					onPress={() => handleFeedback("reportIssue")}
					title={t("report.sessionAnalysis.menuActions.reportIssue")}
				/>
			</Menu>
			<FeedbackDialog
				featureName={featureName}
				visible={showFeedbackDialog}
				handleDismiss={handleDismissFeedback}
				view="session"
				itemId={String(itemId)}
				itemFeedbackRating={
					feedbackType === "reportIssue" ? "negative" : feedbackType
				}
				sessionId={sessionId}
				threadId={itemAIFeedback?.runDetails?.threadId}
				runId={itemAIFeedback?.runDetails?.runId}
				type={feedbackType === "reportIssue" ? "Content Flagged" : undefined}
				feedbackTitle={
					feedbackType === "reportIssue" ? t("dialog.report.title") : undefined
				}
				feedbackSubtitle={
					feedbackType === "reportIssue"
						? t("dialog.report.body")
						: t(defaultFeedbackSubtitle)
				}
				feedbackIcon={
					feedbackType === "reportIssue" ? "flag-outline" : undefined
				}
				feedbackCta={
					feedbackType === "reportIssue" ? t("dialog.report.cta") : undefined
				}
			/>
		</>
	);
};
